import { formatDate } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import type { VivestSelectOption } from '@vivest-dev/design-system-web-components';
import { Steps } from '../../../shared/components/steps/steps';
import { VivestControl } from '../../../core/directives/vivest-control';
import { cpfValidator } from '../../../core/validators/cpf-validator';
import {
  BeneficiarioApi,
  BeneficiarioPayload,
  GrauParentescoApi,
  TipoListaBeneficiario,
} from '../../../core/models/requerimento';
import { LoaderService } from '../../../shared/services/loader-service';
import { RequerimentoService } from '../../../shared/services/requerimento-service';
import { RequerimentoState } from '../../../shared/services/requerimento-state';
import { ToastService } from '../../../shared/services/toast-service';
import {
  distribuirErrosDaApi,
  mensagemComDetalhe,
  ROTA_PAGINA_INDISPONIVEL,
  rotaDoErroRequerimento,
} from '../../../shared/utils/requerimento-erros';

export type GrupoBeneficiario = 'preferenciais' | 'designados';

interface DadosBeneficiario {
  /** Id na API; nulo enquanto a linha não foi incluída. */
  id: number | null;
  nome: string;
  cpf: string;
  dataNascimento: string;
  parentesco: string;
  percentual: string;
}

interface GrupoConfig {
  chave: GrupoBeneficiario;
  tpLista: TipoListaBeneficiario;
  titulo: string;
  descricao: string;
  botaoAdicionar: string;
  /** Usado nos rótulos acessíveis de cada linha (ex.: "Nome do beneficiário preferencial 1"). */
  rotulo: string;
}

const GRUPOS: GrupoConfig[] = [
  {
    chave: 'preferenciais',
    tpLista: 'P',
    titulo: 'Beneficiários Preferenciais',
    descricao:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis eget dignissim risus, sed lobortis ex. ' +
      'Curabitur ac leo fermentum, convallis risus id, suscipit arcu. Suspendisse non nisi blandit, ultrices enim non, egestas tellus.',
    botaoAdicionar: 'Adicionar Beneficiário Preferencial',
    rotulo: 'beneficiário preferencial',
  },
  {
    chave: 'designados',
    tpLista: 'D',
    titulo: 'Beneficiários Designados',
    descricao:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis eget dignissim risus, sed lobortis ex. ' +
      'Curabitur ac leo fermentum, convallis risus id, suscipit arcu. Suspendisse non nisi blandit, ultrices enim non, egestas tellus.',
    botaoAdicionar: 'Adicionar Beneficiário Designado',
    rotulo: 'beneficiário designado',
  },
];

/** Graus de parentesco da base unificada como opções do select (value = id), em ordem alfabética. */
function paraParentescoOptions(graus: GrauParentescoApi[]): VivestSelectOption[] {
  return graus
    .map(({ id, descricao }) => ({ value: String(id), label: descricao }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
}

/** Compara rótulos sem diferenciar maiúsculas: a base unificada manda "CÔNJUGE", um registro antigo pode ter "Cônjuge". */
function mesmoRotulo(a: string, b: string): boolean {
  return a.trim().toLocaleUpperCase('pt-BR') === b.trim().toLocaleUpperCase('pt-BR');
}

const PERCENTUAL_TOTAL = 100;

/** Data como a pessoa digita: dd/mm/aaaa. */
const DATA_DO_CAMPO = /^\d{2}\/\d{2}\/\d{4}$/;

const MENSAGEM_ERRO_SALVAR = 'Não foi possível salvar os beneficiários. Tente novamente.';

/** "1968-03-12T00:00:00" (API) para "12/03/1968" (campo). */
function paraDataDoCampo(data: string): string {
  const [ano, mes, dia] = data.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

/** "12/03/1968" (campo) para "1968-03-12" (API). */
function paraDataDaApi(data: string): string {
  const [dia, mes, ano] = data.split('/');
  return `${ano}-${mes}-${dia}`;
}

function formatarCpf(cpf: string): string {
  return cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
}

/** Campos que a tela edita: se algum mudou, o beneficiário salvo é atualizado. */
function mudou(salvo: BeneficiarioApi, atual: BeneficiarioPayload): boolean {
  return (
    salvo.nom_beneficiario !== atual.nom_beneficiario ||
    salvo.num_cpf_benef !== atual.num_cpf_benef ||
    salvo.dt_nascimento.slice(0, 10) !== atual.dt_nascimento ||
    salvo.id_grau_parentesco !== atual.id_grau_parentesco ||
    salvo.ds_parentesco !== atual.ds_parentesco ||
    salvo.pct_participacao !== atual.pct_participacao
  );
}

@Component({
  imports: [Steps, ReactiveFormsModule, VivestControl],
  selector: 'app-beneficiario',
  styleUrl: './beneficiario.scss',
  templateUrl: './beneficiario.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class Beneficiario implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly loaderService = inject(LoaderService);
  private readonly requerimentoService = inject(RequerimentoService);
  private readonly requerimentoState = inject(RequerimentoState);
  private readonly toastService = inject(ToastService);

  readonly stepAtual = signal(4);
  readonly totalSteps = signal(7);

  protected readonly grupos = GRUPOS;
  /**
   * Graus de parentesco da base unificada (value = id). Ganha o rótulo de um parentesco salvo que não está na lista,
   * para não perder o que veio da API.
   */
  protected readonly parentescoOptions = signal<VivestSelectOption[]>([]);
  /** Valores (ids) que vieram da base unificada; os demais são rótulos salvos fora da lista. */
  private readonly idsDosGraus = new Set<string>();
  protected readonly percentualTotal = PERCENTUAL_TOTAL;

  protected readonly form = this.formBuilder.nonNullable.group({
    preferenciais: this.formBuilder.array<FormGroup>([]),
    designados: this.formBuilder.array<FormGroup>([]),
  });

  /** Valores atuais do formulário como signal, para recalcular os totais a cada alteração. */
  private readonly valores = toSignal(this.form.valueChanges, { initialValue: this.form.getRawValue() });

  private readonly statusDoFormulario = toSignal(this.form.statusChanges, { initialValue: this.form.status });

  /** Preferenciais e designados juntos precisam somar 100% (ex.: 100% + 0% ou 60% + 40%). */
  protected readonly totalGeralValido = computed(
    () => this.grupos.reduce((soma, { chave }) => soma + this.total(chave), 0) === PERCENTUAL_TOTAL,
  );

  /**
   * "Continuar para Documentos" só habilita com a soma dos dois grupos em 100% e com todos os campos dos beneficiários
   * adicionados (nome, CPF, data de nascimento, parentesco e percentual) preenchidos e válidos.
   */
  protected readonly podeContinuar = computed(
    () => this.statusDoFormulario() === 'VALID' && this.totalGeralValido(),
  );

  /** Impede um segundo clique enquanto os beneficiários são gravados. */
  protected readonly salvando = signal(false);

  /** Beneficiários ativos na API, por id: base para saber o que incluir, atualizar e remover ao continuar. */
  private readonly salvos = new Map<number, BeneficiarioApi>();

  async ngOnInit(): Promise<void> {
    this.loaderService.showLoader('Carregando...');
    try {
      const [requerimento, graus] = await Promise.all([
        this.requerimentoState.carregar(),
        this.requerimentoService.listarGrausParentesco(),
      ]);
      graus.forEach(({ id }) => this.idsDosGraus.add(String(id)));
      this.parentescoOptions.set(paraParentescoOptions(graus));
      const beneficiarios = await this.requerimentoService.listarBeneficiarios(requerimento.id);
      this.preencherFormulario(beneficiarios.filter((beneficiario) => beneficiario.fl_ativo));
    } catch (erro) {
      this.router.navigate([rotaDoErroRequerimento(erro) ?? ROTA_PAGINA_INDISPONIVEL]);
    } finally {
      this.loaderService.hideLoader();
    }
  }

  protected linhas(grupo: GrupoBeneficiario): FormArray<FormGroup> {
    return this.form.controls[grupo] as FormArray<FormGroup>;
  }

  protected total(grupo: GrupoBeneficiario): number {
    return (this.valores()[grupo] ?? []).reduce((soma, linha) => soma + (Number(linha?.percentual) || 0), 0);
  }

  protected status(grupo: GrupoBeneficiario, indice: number, campo: keyof DadosBeneficiario): 'default' | 'error' {
    const control = this.linhas(grupo).at(indice).get(campo);
    return control?.invalid && control.touched ? 'error' : 'default';
  }

  /** Limite do calendário: a data de nascimento não pode ser futura (a API recusa). */
  protected readonly hoje = paraDataDaApi(formatDate(new Date(), 'dd/MM/yyyy', 'en-US'));

  /** Data do campo (dd/mm/aaaa) no formato do input de calendário (aaaa-mm-dd); vazia enquanto incompleta. */
  protected dataDoCalendario(grupo: GrupoBeneficiario, indice: number): string {
    const data: string = this.linhas(grupo).at(indice).get('dataNascimento')?.value ?? '';
    return DATA_DO_CAMPO.test(data) ? paraDataDaApi(data) : '';
  }

  /** Data escolhida no calendário vai para o campo no formato digitado (dd/mm/aaaa). */
  protected escolherData(grupo: GrupoBeneficiario, indice: number, event: Event): void {
    const valor = (event.target as HTMLInputElement).value;
    if (!valor) return;

    const control = this.linhas(grupo).at(indice).get('dataNascimento');
    control?.setValue(paraDataDoCampo(valor));
    control?.markAsTouched();
  }

  adicionar(grupo: GrupoBeneficiario) {
    this.linhas(grupo).push(this.criarLinha());
  }

  /** Só tira da tela; a remoção na API acontece ao continuar. */
  remover(grupo: GrupoBeneficiario, indice: number) {
    this.linhas(grupo).removeAt(indice);
  }

  onReturn() {
    this.router.navigate(['/requerimento-beneficio/modalidade-imposto']);
  }

  async onSubmit(): Promise<void> {
    this.form.markAllAsTouched();

    if (!this.podeContinuar() || this.salvando()) {
      return;
    }

    const requerimento = this.requerimentoState.requerimento();
    if (!requerimento) {
      this.router.navigate(['/requerimento-beneficio']);
      return;
    }

    this.salvando.set(true);
    this.loaderService.showLoader('Salvando...');
    try {
      await this.salvarBeneficiarios(requerimento.id);
      this.router.navigate(['/requerimento-beneficio/upload-documento']);
    } catch (erro) {
      this.tratarErroAoSalvar(erro);
    } finally {
      this.loaderService.hideLoader();
      this.salvando.set(false);
    }
  }

  /**
   * A API confere a lista a cada chamada (CPF repetido entre os ativos: 409; soma acima de 100%: 400), então a ordem
   * importa: primeiro remove, depois atualiza os que diminuem o percentual, os demais, e por último inclui. Cada
   * sucesso atualiza `salvos` (e o id da linha incluída): se uma chamada falhar, a nova tentativa refaz só o resto.
   */
  private async salvarBeneficiarios(id: number): Promise<void> {
    const linhas = this.grupos.flatMap((grupo) =>
      this.linhas(grupo.chave).controls.map((linha) => ({
        linha,
        idBeneficiario: (linha.getRawValue() as DadosBeneficiario).id,
        payload: this.paraPayload(grupo, linha.getRawValue() as DadosBeneficiario),
      })),
    );

    const mantidos = new Set(linhas.map(({ idBeneficiario }) => idBeneficiario));
    for (const removido of [...this.salvos.values()].filter((salvo) => !mantidos.has(salvo.id))) {
      await this.requerimentoService.atualizarBeneficiario(id, removido.id, { fl_ativo: false });
      this.salvos.delete(removido.id);
    }

    const diferencaDoPercentual = ({ idBeneficiario, payload }: (typeof linhas)[number]) =>
      payload.pct_participacao - this.salvos.get(idBeneficiario!)!.pct_participacao;
    const alterados = linhas
      .filter(({ idBeneficiario, payload }) => idBeneficiario !== null && mudou(this.salvos.get(idBeneficiario)!, payload))
      .sort((a, b) => diferencaDoPercentual(a) - diferencaDoPercentual(b));
    for (const { idBeneficiario, payload } of alterados) {
      await this.requerimentoService.atualizarBeneficiario(id, idBeneficiario!, payload);
      this.salvos.set(idBeneficiario!, { ...this.salvos.get(idBeneficiario!)!, ...payload });
    }

    for (const { linha, payload } of linhas.filter(({ idBeneficiario }) => idBeneficiario === null)) {
      const incluido = await this.requerimentoService.incluirBeneficiario(id, payload);
      linha.controls['id'].setValue(incluido.id);
      this.salvos.set(incluido.id, incluido);
    }
  }

  private preencherFormulario(beneficiarios: BeneficiarioApi[]): void {
    this.salvos.clear();
    for (const grupo of this.grupos) {
      const linhas = this.linhas(grupo.chave);
      linhas.clear();
      beneficiarios
        .filter((beneficiario) => beneficiario.tp_lista === grupo.tpLista)
        .forEach((beneficiario) => {
          this.salvos.set(beneficiario.id, beneficiario);
          linhas.push(this.criarLinha(this.paraFormulario(beneficiario)));
        });
    }
  }

  private paraFormulario(beneficiario: BeneficiarioApi): DadosBeneficiario {
    return {
      id: beneficiario.id,
      nome: beneficiario.nom_beneficiario,
      cpf: formatarCpf(beneficiario.num_cpf_benef),
      dataNascimento: paraDataDoCampo(beneficiario.dt_nascimento),
      parentesco: this.valorDoParentesco(beneficiario),
      percentual: String(beneficiario.pct_participacao),
    };
  }

  /** Opção da lista: envia o id e o rótulo. Rótulo salvo fora da lista: segue sem id, só com o rótulo. */
  private paraPayload(grupo: GrupoConfig, linha: DadosBeneficiario): BeneficiarioPayload {
    const idGrau = this.idsDosGraus.has(linha.parentesco) ? Number(linha.parentesco) : null;
    return {
      tp_lista: grupo.tpLista,
      nom_beneficiario: linha.nome.trim(),
      num_cpf_benef: linha.cpf.replace(/\D/g, ''),
      dt_nascimento: paraDataDaApi(linha.dataNascimento),
      id_grau_parentesco: idGrau,
      ds_parentesco: this.parentescoOptions().find((opcao) => opcao.value === linha.parentesco)?.label ?? linha.parentesco,
      pct_participacao: Number(linha.percentual),
    };
  }

  /**
   * Seleciona pelo id do grau; sem id (registros antigos), pelo rótulo. Um rótulo fora da lista vira uma opção, para
   * continuar selecionado.
   */
  private valorDoParentesco({ id_grau_parentesco, ds_parentesco }: BeneficiarioApi): string {
    const opcoes = this.parentescoOptions();
    const opcao =
      opcoes.find((item) => id_grau_parentesco !== null && item.value === String(id_grau_parentesco)) ??
      opcoes.find((item) => mesmoRotulo(item.label, ds_parentesco));
    if (opcao) return opcao.value;

    this.parentescoOptions.update((atuais) => [...atuais, { value: ds_parentesco, label: ds_parentesco }]);
    return ds_parentesco;
  }

  /**
   * 409 aqui é CPF repetido na lista ou requerimento fora do rascunho: fica na tela com o motivo da API. O resto segue o
   * padrão das etapas.
   */
  private tratarErroAoSalvar(erro: unknown): void {
    if (erro instanceof HttpErrorResponse && erro.status === 409) {
      this.toastService.show(mensagemComDetalhe(erro, MENSAGEM_ERRO_SALVAR), 'error');
      return;
    }

    const rota = rotaDoErroRequerimento(erro);
    if (rota) {
      this.router.navigate([rota]);
      return;
    }

    distribuirErrosDaApi(erro, {}, MENSAGEM_ERRO_SALVAR).forEach((mensagem) => this.toastService.show(mensagem, 'error'));
  }

  private criarLinha(beneficiario?: DadosBeneficiario): FormGroup {
    return this.formBuilder.nonNullable.group({
      id: [beneficiario?.id ?? null],
      nome: [beneficiario?.nome ?? '', [Validators.required]],
      cpf: [beneficiario?.cpf ?? '', [Validators.required, cpfValidator]],
      dataNascimento: [beneficiario?.dataNascimento ?? '', [Validators.required, Validators.pattern(DATA_DO_CAMPO)]],
      parentesco: [beneficiario?.parentesco ?? '', [Validators.required]],
      percentual: [
        beneficiario?.percentual ?? '',
        [Validators.required, Validators.min(1), Validators.max(PERCENTUAL_TOTAL)],
      ],
    });
  }
}
